import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { postcardShareText } from '@/lib/giraffePaths';
import { copyPostcardImage } from '@/lib/postcardShare';
import { Plus, Edit2, Trash2, Image as ImageIcon, Link2, Check, GripVertical, Copy } from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useConfirmation } from '../shared/ZCard';
import OverflowMenu from '../shared/OverflowMenu';
import ZEmptyState from '../shared/ZEmptyState';
import ZLoadingBar from '../shared/ZLoadingBar';
import CompositionTool from '../shared/CompositionTool';
import { DeleteConfirmDialog } from '../DeleteConfirmDialog';
import { Button } from '@/components/ui/button';

/**
 * GiraffePostcardsTab — travel postcards, managed inside Giraffe.
 *
 * Postcards are a standalone collection (not tied to a single trip). Each is
 * publicly shareable on its own via its public_slug, is public by default, and
 * surfaces as a card in the public /travels gallery next to Countries. Cards
 * are drag-to-reorder; the order is persisted and reflected on the public
 * gallery.
 */
function SortablePostcardCard({ postcard, copiedSlug, imageCopiedSlug, onEdit, onTogglePublic, onCopyLink, onCopyImage, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: postcard.id });
  const slug = postcard.public_slug || postcard.id;
  const hasImage = !!postcard.composed_image_url;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onEdit}
      className="bg-white/[0.02] border border-white/[0.06] overflow-hidden relative group hover:border-[#b0b4c0] transition-all duration-300 cursor-grab active:cursor-grabbing touch-manipulation"
    >
      {/* Stop the menu trigger/items from bubbling to the card's edit-on-click */}
      <div onClick={e => e.stopPropagation()}>
        <OverflowMenu
          publishToggle={{ isPublished: postcard.is_public, onToggle: onTogglePublic }}
          items={[
            ...(hasImage ? [{
              icon: imageCopiedSlug === slug ? <Check size={16} strokeWidth={1.5} /> : <Copy size={16} strokeWidth={1.5} />,
              label: imageCopiedSlug === slug ? 'Copied' : 'Copy image',
              onClick: onCopyImage,
            }] : []),
            {
              icon: copiedSlug === slug ? <Check size={16} strokeWidth={1.5} /> : <Link2 size={16} strokeWidth={1.5} />,
              label: copiedSlug === slug ? 'Copied' : 'Copy to share',
              onClick: onCopyLink,
            },
            { icon: <Edit2 size={16} strokeWidth={1.5} />, label: 'Edit', onClick: onEdit },
            { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: onDelete, destructive: true },
          ]}
        />
      </div>
      {/* Drag hint — visual only */}
      <div className="absolute top-2 left-2 z-10 p-1 text-[#b0b4c0]/30 group-hover:text-[#b0b4c0]/70 transition-colors pointer-events-none">
        <GripVertical size={14} />
      </div>
      <div className="aspect-[4/5] relative">
        {postcard.composed_image_url ? (
          <img src={postcard.composed_image_url} alt={postcard.title || ''} loading="lazy" className="absolute inset-0 w-full h-full object-contain bg-black/30 pointer-events-none" />
        ) : (
          <div className="absolute inset-0 bg-[#0a0a0f] flex items-center justify-center pointer-events-none"><ImageIcon size={32} className="text-white/[0.06]" /></div>
        )}
        {!postcard.is_public && (
          <div className="absolute bottom-2 left-2 bg-[#0a0a0f]/70 backdrop-blur-sm px-2 py-0.5 rounded-full pointer-events-none">
            <span className="text-[10px] font-mono text-[#b0b4c0] tracking-wider uppercase">Hidden</span>
          </div>
        )}
      </div>
      <div className="p-3">
        <p className="text-sm text-[#f0f0f5] truncate pointer-events-none" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{postcard.title || 'Untitled'}</p>
      </div>
    </div>
  );
}

export default function GiraffePostcardsTab() {
  const [postcards, setPostcards] = useState(null);
  const [composing, setComposing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [copiedSlug, setCopiedSlug] = useState(null);
  const [imageCopiedSlug, setImageCopiedSlug] = useState(null);
  const feedback = useConfirmation();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const load = async () => {
    try {
      const { data } = await api.get('/giraffe/postcards');
      setPostcards(Array.isArray(data) ? data : []);
    } catch {
      setPostcards([]);
    }
  };

  useEffect(() => { load(); }, []);

  const togglePublic = async (postcard) => {
    const newVal = !postcard.is_public;
    setPostcards(prev => prev.map(p => p.id === postcard.id ? { ...p, is_public: newVal } : p));
    try {
      await api.put(`/giraffe/postcards/${postcard.id}/publish`, { is_public: newVal });
    } catch {
      setPostcards(prev => prev.map(p => p.id === postcard.id ? { ...p, is_public: !newVal } : p));
      feedback.fail();
    }
  };

  const copyLink = async (postcard) => {
    const slug = postcard.public_slug || postcard.id;
    try {
      await navigator.clipboard.writeText(postcardShareText(postcard));
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(null), 2000);
    } catch {
      feedback.fail();
    }
  };

  const copyImage = async (postcard) => {
    const slug = postcard.public_slug || postcard.id;
    try {
      await copyPostcardImage(postcard);
      setImageCopiedSlug(slug);
      setTimeout(() => setImageCopiedSlug(null), 2000);
    } catch {
      feedback.fail();
    }
  };


  const confirmDelete = () => {
    const { id } = deleteConfirm;
    api.delete(`/giraffe/postcards/${id}`)
      .then(() => { setPostcards(prev => prev.filter(p => p.id !== id)); feedback.confirm(); })
      .catch(() => feedback.fail())
      .finally(() => setDeleteConfirm({ open: false, id: null, name: '' }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setPostcards(prev => {
      const oldIndex = prev.findIndex(p => p.id === active.id);
      const newIndex = prev.findIndex(p => p.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const reordered = arrayMove(prev, oldIndex, newIndex);
      api.post('/giraffe/postcards/reorder', { order: reordered.map(p => p.id) })
        .catch(() => { feedback.fail(); load(); });
      return reordered;
    });
  };

  if (postcards === null) {
    return (
      <div style={{ padding: '48px 24px' }}>
        <ZLoadingBar accent="#10B981" loading={true} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-6">
        <Button onClick={() => setComposing(true)} className="bg-[#10B981] hover:bg-[#10B981]/90 text-white rounded-full px-4 text-xs">
          <Plus size={14} className="mr-1.5" /> New Postcard
        </Button>
      </div>

      {postcards.length === 0 ? (
        <ZEmptyState
          mark="G"
          accent="#10B981"
          message="No postcards yet"
          hint="Compose one with the New Postcard button."
        />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={postcards.map(p => p.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-[2px]">
              {postcards.map(postcard => (
                <SortablePostcardCard
                  key={postcard.id}
                  postcard={postcard}
                  copiedSlug={copiedSlug}
                  imageCopiedSlug={imageCopiedSlug}
                  onEdit={() => setComposing(postcard)}
                  onTogglePublic={() => togglePublic(postcard)}
                  onCopyLink={() => copyLink(postcard)}
                  onCopyImage={() => copyImage(postcard)}
                  onDelete={() => setDeleteConfirm({ open: true, id: postcard.id, name: postcard.title || 'this postcard' })}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Composition tool */}
      {composing && (
        <CompositionTool
          mode="postcard"
          editingId={composing !== true ? composing.id : undefined}
          initialData={composing !== true ? { title: composing.title || '', body: composing.body || '', field3: composing.field3 || '', story: composing.story || '', gallery: composing.gallery || [], trip_id: composing.trip_id ?? null } : undefined}
          photo={composing !== true && composing.source_photo_url ? { url: composing.source_photo_url } : undefined}
          onClose={() => setComposing(false)}
          onSave={() => { load(); }}
        />
      )}

      <DeleteConfirmDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null, name: '' })}
        onConfirm={confirmDelete}
        itemName={deleteConfirm.name}
        itemType="postcard"
      />
    </div>
  );
}
